import {
  generatePath,
  Navigate,
  RouteObject,
  useParams
} from 'react-router-dom';
import loadable from '@loadable/component';

import { ScreenerSettingName } from '@/hooks/screener-settings/screenerSettingName';
import { ExacareFeature, FeatureFlagService } from '@/utils/featureFlagService';

import { ScreenerSettingRouteGuard } from '../ScreenerSettingRouteGuard';

const CRMScreener = loadable(() => import('@/pages/CRM/Screener')); // prettier-ignore
const CRMScreenerAllLeads = loadable(
  () => import('@/pages/CRM/Screener/Leads/AllLeads/AllLeads')
); // prettier-ignore
const CRMScreenerESign = loadable(() => import('@/pages/CRM/Screener/ESign')); // prettier-ignore
const CRMScreenerEfax = loadable(
  () => import('@/pages/CRM/Screener/eFax/components/AllEfaxesDataGrid')
); // prettier-ignore
const CRMScreenerLeadWaitlist = loadable(
  () => import('@/pages/CRM/Screener/LeadWaitlist')
); // prettier-ignore
const CRMScreenerSingleLead = loadable(
  () => import('@/pages/CRM/Screener/Leads/SingleLead/SingleLead')
); // prettier-ignore
const CRMScreenerRules = loadable(() => import('@/pages/CRM/Screener/Rules')); // prettier-ignore
const CRMScreenerAdmissionFlags = loadable(
  () => import('@/pages/CRM/Screener/AdmissionFlags')
); // prettier-ignore
const CRMScreenerSettings = loadable(
  () => import('@/pages/AdminHub/ScreenerSettings')
); // prettier-ignore
const CRMScreenerSettingsUsageOverview = loadable(
  () => import('@/pages/AdminHub/ScreenerSettings/UsageOverview')
); // prettier-ignore
const CRMScreenerSettingsUsageIntegrations = loadable(
  () => import('@/pages/AdminHub/ScreenerSettings/Integrations')
); // prettier-ignore
const CRMScreenerSingleLeadPDPM = loadable(
  () =>
    import(
      '@/pages/CRM/Screener/Leads/SingleLead/components/SingleLeadAnalysisTabs/MedicareTab'
    )
); // prettier-ignore
const CRMScreenerSingleLeadReimbursementEstimator = loadable(
  () =>
    import(
      '@/pages/CRM/Screener/Leads/SingleLead/components/SingleLeadAnalysisTabs/ReimbursementEstimatorTab'
    )
); // prettier-ignore
const CRMScreenerSingleLeadUnifiedInsurance = loadable(
  () =>
    import(
      '@/pages/CRM/Screener/Leads/SingleLead/components/SingleLeadAnalysisTabs/UnifiedInsuranceTab'
    )
); // prettier-ignore
const CRMScreenerSingleLeadUnifiedInsuranceCreate = loadable(
  () =>
    import(
      '@/pages/CRM/Screener/Leads/SingleLead/components/SingleLeadAnalysisTabs/UnifiedInsuranceTab/UnifiedInsuranceCreate'
    )
); // prettier-ignore
const CRMScreenerSingleLeadUnifiedInsuranceView = loadable(
  () =>
    import(
      '@/pages/CRM/Screener/Leads/SingleLead/components/SingleLeadAnalysisTabs/UnifiedInsuranceTab/UnifiedVerificationView'
    )
); // prettier-ignore
const CRMScreenerSingleLeadCoverageRequestsList = loadable(
  () =>
    import(
      '@/pages/CRM/Screener/Leads/SingleLead/components/SingleLeadAnalysisTabs/SmartResearchTab/components/SmartResearchList'
    )
); // prettier-ignore
const CRMScreenerSingleLeadCoverageRequestsView = loadable(
  () =>
    import(
      '@/pages/CRM/Screener/Leads/SingleLead/components/SingleLeadAnalysisTabs/SmartResearchTab/components/SmartResearchDetails'
    )
); // prettier-ignore

const CRMScreenerSingleLeadTasks = loadable(
  () =>
    import(
      '@/pages/CRM/Screener/Leads/SingleLead/components/SingleLeadAnalysisTabs/TasksTab'
    )
); // prettier-ignore
const CRMScreenerSingleLeadActivity = loadable(
  () =>
    import(
      '@/pages/CRM/Screener/Leads/SingleLead/components/SingleLeadAnalysisTabs/CommentsTab'
    )
); // prettier-ignore
const CRMScreenerSingleLeadDetailsMobile = loadable(
  () =>
    import(
      '@/pages/CRM/Screener/Leads/SingleLead/components/SingleLeadAnalysisTabs/DetailsTab'
    )
); // prettier-ignore
const CRMScreenerSingleLeadAdmissionsPackageMobile = loadable(
  () =>
    import(
      '@/pages/CRM/Screener/Leads/SingleLead/components/SingleLeadAnalysisTabs/AdmissionsPackageTab'
    )
); // prettier-ignore
const CRMSCreenerAIAnalysis = loadable(
  () =>
    import(
      '@/pages/CRM/Screener/Leads/SingleLead/components/SingleLeadAnalysisTabs/AIAnalysisTab'
    )
); // prettier-ignore
const CRMSCreenerSingleLeadMedications = loadable(
  () =>
    import(
      '@/pages/CRM/Screener/Leads/SingleLead/components/SingleLeadAnalysisTabs/ReportTab'
    )
); // prettier-ignore
const CRMScreenerSingleLeadReport = loadable(
  () =>
    import(
      '@/pages/CRM/Screener/Leads/SingleLead/components/SingleLeadAnalysisTabs/ReportTab'
    )
); // prettier-ignore
const CRMScreenerSingleLeadFacesheet = loadable(
  () =>
    import(
      '@/pages/CRM/Screener/Leads/SingleLead/components/SingleLeadAnalysisTabs/LeadFacesheet'
    )
); // prettier-ignore
const CRMSCreenerSingleLeadSOffender = loadable(
  () =>
    import(
      '@/pages/CRM/Screener/Leads/SingleLead/components/SingleLeadAnalysisTabs/SOffenderTab'
    )
); // prettier-ignore
const CRMScreenerSingleLeadHospitalChat = loadable(
  () =>
    import(
      '@/pages/CRM/Screener/Leads/SingleLead/components/SingleLeadAnalysisTabs/HospitalChatTab/HospitalChatTab'
    )
); // prettier-ignore
const CRMScreenerSingleLeadRocChat = loadable(
  () =>
    import(
      '@/pages/CRM/Screener/Leads/SingleLead/components/SingleLeadAnalysisTabs/RocChatTab/RocChatTab'
    )
); // prettier-ignore
const CRMScreenerSettingsDocumentIgnoreList = loadable(
  () => import('@/pages/AdminHub/ScreenerSettings/DocumentIgnoreList')
); // prettier-ignore
const CRMScreenerSettingsApprovedHospitalSources = loadable(
  () => import('@/pages/AdminHub/ScreenerSettings/ApprovedHospitalSources')
); // prettier-ignore
const CRMScreenerSingleLeadPointClickCareUpdates = loadable(
  () =>
    import(
      '@/pages/CRM/Screener/Leads/SingleLead/components/SingleLeadAnalysisTabs/PointClickCareTab'
    )
); // prettier-ignore
const CRMScreenerSingleLeadMatrixCare = loadable(
  () =>
    import(
      '@/pages/CRM/Screener/Leads/SingleLead/components/SingleLeadAnalysisTabs/MatrixCareTab'
    )
); // prettier-ignore
const CRMScreenerSingleLeadHchb = loadable(
  () =>
    import(
      '@/pages/CRM/Screener/Leads/SingleLead/components/SingleLeadAnalysisTabs/HchbTab'
    )
); // prettier-ignore
const CRMScreenerSingleLeadPalPreAuth = loadable(
  () =>
    import(
      '@/pages/CRM/Screener/Leads/SingleLead/components/SingleLeadAnalysisTabs/PalPreAuthTab'
    )
); // prettier-ignore
const CRMScreenerSingleLeadESignRequestSetup = loadable(
  () =>
    import(
      '@/pages/CRM/Screener/Leads/SingleLead/components/ESignRequest/ESignRequestSetup'
    )
); // prettier-ignore
const CRMScreenerSingleLeadESignRequestDetail = loadable(
  () =>
    import(
      '@/pages/CRM/Screener/Leads/SingleLead/components/ESignRequest/ESignRequestDetail'
    )
); // prettier-ignore

export enum CRMScreenerRoutes {
  CRMScreener = '/crm/screener',
  CRMScreenerAllLeads = '/crm/screener/leads',
  CRMScreenerESign = '/crm/screener/esign',
  CRMScreenerEfax = '/crm/screener/efax',
  CRMScreenerSingleLead = '/crm/screener/leads/:lead_id',
  CRMScreenerSingleLeadActivity = '/crm/screener/leads/:lead_id/activities-and-comments',
  CRMScreenerSingleLeadPDPM = '/crm/screener/leads/:lead_id/medicare',
  CRMScreenerSingleLeadMedications = '/crm/screener/leads/:lead_id/medications',
  CRMScreenerSingleLeadInsuranceEligibility = '/crm/screener/leads/:lead_id/insurance-eligibility',
  CRMScreenerSingleLeadUnifiedInsurance = '/crm/screener/leads/:lead_id/insurance',
  CRMScreenerSingleLeadUnifiedInsuranceCreate = '/crm/screener/leads/:lead_id/insurance/create',
  CRMScreenerSingleLeadUnifiedInsuranceView = '/crm/screener/leads/:lead_id/insurance/view/:result_id',
  CRMScreenerSingleLeadInsuranceEligibilityCreate = '/crm/screener/leads/:lead_id/insurance-eligibility/create',
  CRMScreenerSingleLeadInsuranceEligibilityView = '/crm/screener/leads/:lead_id/insurance-eligibility/view/:check_id',
  CRMScreenerSingleLeadSmartResearch = '/crm/screener/leads/:lead_id/smart-research',
  CRMScreenerSingleLeadSmartResearchView = '/crm/screener/leads/:lead_id/smart-research/view/:execution_id',
  CRMScreenerSingleLeadReimbursementEstimator = '/crm/screener/leads/:lead_id/reimbursement-estimator',
  CRMScreenerSingleLeadTasks = '/crm/screener/leads/:lead_id/tasks',
  CRMScreenerSingleLeadDetailsMobile = '/crm/screener/leads/:lead_id/details',
  CRMScreenerSingleLeadAdmissionsPackageMobile = '/crm/screener/leads/:lead_id/admissions-package',
  CRMScreenerSingleLeadAIAnalysisDataPulls = '/crm/screener/leads/:lead_id/data-pulls',
  CRMScreenerSingleLeadAIAnalysisRules = '/crm/screener/leads/:lead_id/rules',
  CRMScreenerSingleLeadSOffenderScreen = '/crm/screener/leads/:lead_id/s-offender-screen',
  CRMScreenerSingleLeadReport = '/crm/screener/leads/:lead_id/report',
  CRMScreenerSingleLeadFacesheet = '/crm/screener/leads/:lead_id/facesheet',
  CRMScreenerSingleLeadHospitalChat = '/crm/screener/leads/:lead_id/hospital-chat',
  CRMScreenerSingleLeadRocChat = '/crm/screener/leads/:lead_id/roc-chat',
  CRMScreenerLeadWaitlist = '/crm/screener/lead-waitlist',
  CRMScreenerRules = '/crm/screener/rules',
  CRMScreenerMedRules = '/crm/screener/rules/med-rules',
  CRMScreenerRulesDataPulls = '/crm/screener/rules/data-pulls',
  CRMScreenerAdmissionAlerts = '/crm/screener/admission-alerts',
  CRMScreenerAdmissionFlags = '/crm/screener/admission-flags',
  CRMScreenerSettings = '/crm/screener/settings',
  CRMScreenerSettingsUsageOverview = '/crm/screener/settings/usage-overview',
  CRMScreenerSettingsIntegrations = '/crm/screener/settings/integrations',
  CRMScreenerSettingsDocumentIgnoreList = '/crm/screener/settings/document-ignore-list',
  CRMScreenerSettingsApprovedHospitalSources = '/crm/screener/settings/approved-hospital-sources',
  CRMScreenerUsageOverview = '/crm/screener/usage-overview',
  CRMScreenerResidentsOccupancyHistory = '/crm/screener/residents/occupancy-history',
  CRMScreenerSingleLeadPointClickCareUpdates = '/crm/screener/leads/:lead_id/point-click-care-updates',
  CRMScreenerSingleLeadMatrixCare = '/crm/screener/leads/:lead_id/matrix-care',
  CRMScreenerSingleLeadHchb = '/crm/screener/leads/:lead_id/homecare-homebase',
  CRMScreenerSingleLeadPalPreAuth = '/crm/screener/leads/:lead_id/pal-pre-auth',
  CRMScreenerSingleLeadESignNew = '/crm/screener/leads/:lead_id/esign/new',
  CRMScreenerSingleLeadESignRequest = '/crm/screener/leads/:lead_id/esign/:request_id'
}

const LegacyInsuranceEligibilityRedirect = ({
  to
}: {
  to: CRMScreenerRoutes;
}) => {
  const params = useParams();
  const redirectParams = {
    lead_id: params.lead_id ?? null,
    result_id: params.result_id ?? params.check_id ?? null,
    check_id: params.check_id ?? null,
    execution_id: params.execution_id ?? null,
    request_id: params.request_id ?? null
  };

  return <Navigate to={generatePath(to, redirectParams)} replace />;
};

export const getCrmScreenerRoutes = (): RouteObject[] => {
  if (!FeatureFlagService.isEnabled(ExacareFeature.CRM_SCREENER)) {
    return [];
  }

  return [
    {
      path: CRMScreenerRoutes.CRMScreener,
      element: <CRMScreener />,
      children: [
        {
          path: CRMScreenerRoutes.CRMScreenerAllLeads,
          element: <CRMScreenerAllLeads />
        },
        {
          path: CRMScreenerRoutes.CRMScreenerEfax,
          element: <CRMScreenerEfax />
        },
        {
          path: CRMScreenerRoutes.CRMScreenerLeadWaitlist,
          element: <CRMScreenerLeadWaitlist />
        },
        {
          path: CRMScreenerRoutes.CRMScreenerRules,
          element: (
            <ScreenerSettingRouteGuard
              settingName={ScreenerSettingName.FacTagCriteriaTargeting}
              requiredValue="disabled"
              redirectTo={CRMScreenerRoutes.CRMScreenerAllLeads}
            >
              <CRMScreenerRules />
            </ScreenerSettingRouteGuard>
          ),
          children: [
            {
              path: CRMScreenerRoutes.CRMScreenerRulesDataPulls,
              element: <CRMScreenerRules />
            },
            ...(FeatureFlagService.isEnabled(
              ExacareFeature.CRM_SCREENER_TRACKED_MEDS_RULES
            )
              ? [
                  {
                    path: CRMScreenerRoutes.CRMScreenerMedRules,
                    element: <CRMScreenerRules />
                  }
                ]
              : [])
          ]
        },

        {
          path: CRMScreenerRoutes.CRMScreenerAdmissionAlerts,
          element: <CRMScreenerAdmissionFlags />
        },
        {
          path: CRMScreenerRoutes.CRMScreenerAdmissionFlags,
          element: <CRMScreenerAdmissionFlags />
        },
        {
          path: CRMScreenerRoutes.CRMScreenerSettings,
          element: <CRMScreenerSettings />,
          children: [
            {
              path: CRMScreenerRoutes.CRMScreenerSettingsUsageOverview,
              element: <CRMScreenerSettingsUsageOverview />
            },
            {
              path: CRMScreenerRoutes.CRMScreenerSettingsIntegrations,
              element: <CRMScreenerSettingsUsageIntegrations />
            },
            {
              path: CRMScreenerRoutes.CRMScreenerSettingsDocumentIgnoreList,
              element: <CRMScreenerSettingsDocumentIgnoreList />
            },
            {
              path: CRMScreenerRoutes.CRMScreenerSettingsApprovedHospitalSources,
              element: <CRMScreenerSettingsApprovedHospitalSources />
            }
          ]
        }
      ]
    },
    {
      path: CRMScreenerRoutes.CRMScreenerSingleLead,
      element: <CRMScreenerSingleLead />,
      children: [
        {
          path: CRMScreenerRoutes.CRMScreenerSingleLeadDetailsMobile,
          element: <CRMScreenerSingleLeadDetailsMobile />
        },
        {
          path: CRMScreenerRoutes.CRMScreenerSingleLeadAdmissionsPackageMobile,
          element: <CRMScreenerSingleLeadAdmissionsPackageMobile />
        },
        {
          path: CRMScreenerRoutes.CRMScreenerSingleLeadMedications,
          element: <CRMSCreenerSingleLeadMedications />
        },
        {
          path: CRMScreenerRoutes.CRMScreenerSingleLeadActivity,
          element: <CRMScreenerSingleLeadActivity />
        },
        {
          path: CRMScreenerRoutes.CRMScreenerSingleLeadPDPM,
          element: <CRMScreenerSingleLeadPDPM />
        },
        {
          path: CRMScreenerRoutes.CRMScreenerSingleLeadPointClickCareUpdates,
          element: <CRMScreenerSingleLeadPointClickCareUpdates />
        },
        {
          path: CRMScreenerRoutes.CRMScreenerSingleLeadMatrixCare,
          element: <CRMScreenerSingleLeadMatrixCare />
        },
        {
          path: CRMScreenerRoutes.CRMScreenerSingleLeadHchb,
          element: <CRMScreenerSingleLeadHchb />
        },
        {
          path: CRMScreenerRoutes.CRMScreenerSingleLeadReimbursementEstimator,
          element: <CRMScreenerSingleLeadReimbursementEstimator />
        },
        {
          path: CRMScreenerRoutes.CRMScreenerSingleLeadTasks,
          element: <CRMScreenerSingleLeadTasks />
        },
        {
          path: CRMScreenerRoutes.CRMScreenerSingleLeadAIAnalysisDataPulls,
          element: <CRMSCreenerAIAnalysis qualifierFilter="data-pulls" />
        },
        {
          path: CRMScreenerRoutes.CRMScreenerSingleLeadAIAnalysisRules,
          element: <CRMSCreenerAIAnalysis qualifierFilter="rules" />
        },
        {
          path: CRMScreenerRoutes.CRMScreenerSingleLeadSOffenderScreen,
          element: <CRMSCreenerSingleLeadSOffender />
        },
        {
          path: CRMScreenerRoutes.CRMScreenerSingleLeadReport,
          element: <CRMScreenerSingleLeadReport />
        },
        {
          path: CRMScreenerRoutes.CRMScreenerSingleLeadFacesheet,
          element: <CRMScreenerSingleLeadFacesheet />
        },
        {
          path: CRMScreenerRoutes.CRMScreenerSingleLeadHospitalChat,
          element: <CRMScreenerSingleLeadHospitalChat />
        },
        {
          path: CRMScreenerRoutes.CRMScreenerSingleLeadRocChat,
          element: <CRMScreenerSingleLeadRocChat />
        },
        {
          path: CRMScreenerRoutes.CRMScreenerSingleLeadUnifiedInsurance,
          children: [
            {
              index: true,
              element: <CRMScreenerSingleLeadUnifiedInsurance />
            },
            {
              path: CRMScreenerRoutes.CRMScreenerSingleLeadUnifiedInsuranceCreate,
              element: <CRMScreenerSingleLeadUnifiedInsuranceCreate />
            },
            {
              path: CRMScreenerRoutes.CRMScreenerSingleLeadUnifiedInsuranceView,
              element: <CRMScreenerSingleLeadUnifiedInsuranceView />
            }
          ]
        },
        {
          path: CRMScreenerRoutes.CRMScreenerSingleLeadInsuranceEligibility,
          children: [
            {
              index: true,
              element: (
                <LegacyInsuranceEligibilityRedirect
                  to={CRMScreenerRoutes.CRMScreenerSingleLeadUnifiedInsurance}
                />
              )
            },
            {
              path: CRMScreenerRoutes.CRMScreenerSingleLeadInsuranceEligibilityCreate,
              element: (
                <LegacyInsuranceEligibilityRedirect
                  to={
                    CRMScreenerRoutes.CRMScreenerSingleLeadUnifiedInsuranceCreate
                  }
                />
              )
            },
            {
              path: CRMScreenerRoutes.CRMScreenerSingleLeadInsuranceEligibilityView,
              element: (
                <LegacyInsuranceEligibilityRedirect
                  to={
                    CRMScreenerRoutes.CRMScreenerSingleLeadUnifiedInsuranceView
                  }
                />
              )
            },
            {
              path: '*',
              element: (
                <LegacyInsuranceEligibilityRedirect
                  to={CRMScreenerRoutes.CRMScreenerSingleLeadUnifiedInsurance}
                />
              )
            }
          ]
        },
        {
          path: CRMScreenerRoutes.CRMScreenerSingleLeadPalPreAuth,
          element: <CRMScreenerSingleLeadPalPreAuth />
        },
        {
          path: CRMScreenerRoutes.CRMScreenerSingleLeadSmartResearch,
          children: [
            {
              index: true,
              element: <CRMScreenerSingleLeadCoverageRequestsList />
            },
            {
              path: CRMScreenerRoutes.CRMScreenerSingleLeadSmartResearchView,
              element: <CRMScreenerSingleLeadCoverageRequestsView />
            },
            {
              path: '*',
              element: <CRMScreenerSingleLeadCoverageRequestsList />
            }
          ]
        }
      ]
    },
    ...(FeatureFlagService.isEnabled(ExacareFeature.CRM_ESIGN)
      ? [
          {
            path: CRMScreenerRoutes.CRMScreenerESign,
            element: <CRMScreenerESign />
          },
          {
            path: CRMScreenerRoutes.CRMScreenerSingleLeadESignNew,
            element: <CRMScreenerSingleLeadESignRequestSetup />
          },
          {
            path: CRMScreenerRoutes.CRMScreenerSingleLeadESignRequest,
            element: <CRMScreenerSingleLeadESignRequestDetail />
          }
        ]
      : [])
  ];
};
